import type React from "react"
import { User, Home, BookOpen, LogOut, Menu } from "lucide-react"
import { Button } from "../pages/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "../pages/ui/sheet"
import { Avatar, AvatarFallback, AvatarImage } from "../pages/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../pages/ui/dropdown-menu";
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from "../services/api"

export default function FormateurLayout({
  children,
}: {
  children: React.ReactNode
}) {

  const { user, logout, loading } = useAuth()

  const handleLogout = async () => {
    await logout();
    window.location.href = '/signin'; // optional redirect after logout
  };

  // Show loading while user data is being fetched
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00088E] mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  // If no user after loading is complete, show error state
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">Erreur: Utilisateur non trouvé</p>
          <a href="/signin" className="text-blue-600 hover:underline">
            Retourner à la connexion
          </a>
        </div>
      </div>
    );
  }

  // Données utilisateur - with safe fallbacks
  const UserData = {
    name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Utilisateur",
    email: user?.email || "email@example.com",
    avatar: user?.photo || null,
    joinedDate: user?.createdAt || null,
  };

  const getImageUrl = (imagePath: string | null | undefined): string | undefined => {

    if (!imagePath || imagePath === "/placeholder.svg") {
      return undefined; // Let Avatar component show fallback
    }

    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    return `${API_BASE_URL}${imagePath}`;
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* Header spécifique au dashboard */}
      <header className="border-b bg-white dark:bg-gray-950 sticky top-0 z-10">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <a href="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Elille Academy Logo" width={100} height={70} />
            </a>
          </div>

          <div className="flex items-center gap-4">
            {/* Menu mobile */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64">
                <div className="flex flex-col gap-6 py-6">
                  <a href="/" className="flex items-center gap-2">
                    <img src="/profile.png?height=30&width=30" alt="Elille Academy Logo" width={30} height={30} />
                    <span className="text-lg font-serif font-bold">Elille Academy</span>
                  </a>

                  <div className="flex flex-col gap-2 py-2">
                    <div className="flex items-center gap-2 mb-4">
                      <Avatar>
                        <AvatarImage
                          src={getImageUrl(UserData.avatar)}
                          alt={UserData.name}
                          onError={(e) => (e.currentTarget.style.display = 'none')}
                        />
                        <AvatarFallback>
                          {UserData.name?.charAt(0).toUpperCase() ?? 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{UserData.name}</p>
                        <p className="text-xs text-gray-500">{UserData.email}</p>
                      </div>
                    </div>

                    <nav className="flex flex-col gap-1">
                      <a
                        href="/formateur"
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
                      >
                        <Home className="h-4 w-4" />
                        Tableau de bord
                      </a>
                      <a
                        href="/formateur/about"
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
                      >
                        <User className="h-4 w-4" />
                        Mon profil
                      </a>
                      <a
                        href="/formateur/courses"
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
                      >
                        <BookOpen className="h-4 w-4" />
                        Mes formations
                      </a>
                    </nav>

                    <div className="mt-6 pt-6 border-t">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800 w-full text-left"
                      >
                        <LogOut className="h-4 w-4" />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Profil utilisateur */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar>
                    <AvatarImage
                      src={getImageUrl(UserData.avatar)}
                      alt={UserData.name}
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                    <AvatarFallback>
                      {UserData.name?.charAt(0).toUpperCase() ?? 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{UserData.name}</p>
                    <p className="text-xs leading-none text-muted-foreground">{UserData.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="/formateur">Tableau de bord</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/formateur/about">Mon profil</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/formateur/courses">Mes formations</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/formateur/edit">Paramètres</a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="/">Retour au site</a>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Déconnexion</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar de navigation (visible uniquement sur desktop) */}
        <aside className="hidden md:flex w-64 flex-col border-r bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-col gap-2 p-4">
            <div className="py-2">
              <h2 className="text-lg font-serif font-semibold">Mon espace</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gérez votre compte</p>
            </div>
            <nav className="flex flex-col gap-1">
              <a
                href="/formateur"
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
              >
                <Home className="h-4 w-4" />
                Tableau de bord
              </a>
              <a
                href="/formateur/about"
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
              >
                <User className="h-4 w-4" />
                Mon profil
              </a>
              <a
                href="/formateur/courses"
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
              >
                <BookOpen className="h-4 w-4" />
                Mes formations
              </a>
            </nav>
            <div className="mt-auto pt-6">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800 w-full text-left"
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </button>
            </div>
          </div>
        </aside>

        {/* Contenu principal */}
        <main className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">{children}</main>
      </div>
    </div>
  )
}
